import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, message, Spin } from "antd";
import {
    fetch_all_countries,
    get_state_by_country,
    get_cities_by_state,
    create_b2b_client_location,
    update_b2b_client_location,
} from "../../endpoints/API";

const { Option } = Select;

const AddB2BClientLocationModal = ({
    open,
    onClose,
    onSuccess,
    b2bClientId,
    initialValues,
    locationId,
}) => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            try {
                const res = await fetch_all_countries();
                if (res?.status === 200) setCountries(res.data);
            } catch (err) {
                console.error("Error fetching countries:", err);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (initialValues) {
            const loc = initialValues.location || {};
            form.setFieldsValue({
                address_identifier: loc.address_identifier,
                apartment_road_area: loc.apartment_road_area,
                house_flat_floor_no: loc.house_flat_floor_no,
                country: loc.country?.id,
                state: loc.state?.id,
                city: loc.city?.id,
            });

            if (loc.country?.id) fetchStates(loc.country.id);
            if (loc.state?.id) fetchCities(loc.state.id);
        } else {
            form.resetFields();
        }
    }, [initialValues]);

    const fetchStates = async (countryId) => {
        try {
            const res = await get_state_by_country(countryId);
            const data = res?.data || [];
            setStates(
                data
                    .filter((s) => s.status === "enable")
                    .map((s) => ({ value: s.id, label: s.name }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    const fetchCities = async (stateId) => {
        try {
            const res = await get_cities_by_state(stateId);
            const data = res?.data || [];
            setCities(
                data
                    .filter((c) => c.status === "enable")
                    .map((c) => ({ value: c.id, label: c.name }))
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleCountryChange = (id) => {
        form.setFieldsValue({ state: undefined, city: undefined });
        setStates([]);
        setCities([]);
        fetchStates(id);
    };

    const handleStateChange = (id) => {
        form.setFieldsValue({ city: undefined });
        setCities([]);
        fetchCities(id);
    };

    const onFinish = async (values) => {
        const selectedCountry = countries.find((c) => c.id === values.country);
        const selectedState = states.find((s) => s.value === values.state);
        const selectedCity = cities.find((c) => c.value === values.city);

        const payload = {
            b2b_client: b2bClientId,
            location: {
                address_identifier: values.address_identifier,
                apartment_road_area: values.apartment_road_area,
                house_flat_floor_no: values.house_flat_floor_no,
                country: values.country,
                country_name: selectedCountry?.name || null,
                state: values.state,
                state_name: selectedState?.label || null,
                city: values.city,
                city_name: selectedCity?.label || null,
                status: "enable",
            },
        };

        setIsSubmitting(true);
        try {
            let res;

            if (locationId) {
                res = await update_b2b_client_location(locationId, {
                    address_identifier: values.address_identifier,
                    apartment_road_area: values.apartment_road_area,
                    house_flat_floor_no: values.house_flat_floor_no,
                    country: values.country,
                    country_name: selectedCountry?.name || null,
                    state: values.state,
                    state_name: selectedState?.label || null,
                    city: values.city,
                    city_name: selectedCity?.label || null,
                    status: "enable",
                });
            } else {
                res = await create_b2b_client_location(payload);
            }

            if (res?.status === 200 || res?.status === 201) {
                message.success(locationId ? "Location updated!" : "Location added!");
                form.resetFields();
                onSuccess?.();
                onClose();
            }
        } catch (err) {
            console.error(err);
            message.error(locationId ? "Failed to update location" : "Failed to add location");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            title={locationId ? "Edit Client Location" : "Add Client Location"}
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <Form layout="vertical" form={form} onFinish={onFinish}>
                <Form.Item
                    name="address_identifier"
                    label="Address Identifier"
                    rules={[{ required: true }]}
                >
                    <Input placeholder="E.g., Office, Warehouse" />
                </Form.Item>

                <Form.Item
                    name="apartment_road_area"
                    label="Apartment / Road / Area"
                    rules={[{ required: true }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="house_flat_floor_no"
                    label="House / Flat / Floor No."
                    rules={[{ required: true }]}
                >
                    <Input />
                </Form.Item>

                <div className="flex gap-4 flex-wrap">
                    <Form.Item
                        name="country"
                        label="Country"
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                    >
                        <Select
                            placeholder="Select Country"
                            onChange={handleCountryChange}
                            options={countries.map((c) => ({ value: c.id, label: c.name }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="state"
                        label="State"
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                    >
                        <Select
                            placeholder="Select State"
                            onChange={handleStateChange}
                            options={states}
                        />
                    </Form.Item>

                    <Form.Item
                        name="city"
                        label="City"
                        rules={[{ required: true }]}
                        style={{ flex: 1 }}
                    >
                        <Select placeholder="Select City" options={cities} />
                    </Form.Item>
                </div>

                <Button type="primary" htmlType="submit" disabled={isSubmitting} block>
                    {isSubmitting ? <Spin size="small" /> : locationId ? "Update Location" : "Add Location"}
                </Button>
            </Form>
        </Modal>
    );
};

export default AddB2BClientLocationModal;
